import { useT } from '../context/LanguageContext';
import React, { useState } from 'react';
import { Info, HelpCircle } from 'lucide-react';
import { SpeakerDialog } from './SpeakerDialog';
import { useContent, DEFAULT_SPEAKERS, DEFAULT_HOSTS } from '../context/ContentContext';
import { EditButton } from './admin/EditorShell';
import { PeopleEditor } from './admin/PeopleEditor';
import Updates from './Updates';
import { pictureUrl } from '../lib/api';

const toSpeaker = (p) => ({ ...p, image: pictureUrl(p.image), bio: p.bio || [], socials: p.instagram ? [{ type: 'instagram', href: p.instagram }] : [] });

const ComingSoonArt = () => (
  <div className="w-full h-full relative overflow-hidden bg-[#120c1b]" data-testid="speaker-coming-soon-art">
    <div className="absolute inset-0 grid grid-cols-4 grid-rows-5 opacity-[0.07]">
      {Array.from({ length: 20 }).map((_, i) => (
        <div key={i} className="flex items-center justify-center text-white font-bold text-5xl select-none">?</div>
      ))}
    </div>
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="text-[#ff2e2e] font-bold text-[9rem] md:text-[12rem] leading-none drop-shadow-[0_0_40px_rgba(255,46,46,0.5)]" style={{ fontFamily: "'Oktah Round', sans-serif" }}>?</span>
    </div>
  </div>
);

const Speakers = () => {
  const { t } = useT();
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const { content } = useContent();
  const speakers = (content.speakers || DEFAULT_SPEAKERS).map(toSpeaker);
  const hosts = (content.hosts || DEFAULT_HOSTS).map(toSpeaker);
  const cols = speakers.length >= 3 ? 'md:grid-cols-3 md:max-w-6xl' : speakers.length === 2 ? 'md:grid-cols-2 md:max-w-4xl' : 'md:max-w-sm';

  return (
    <section id="talere" className="bg-black py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-start justify-between gap-4 mb-8 md:mb-12">
          <h2 className="text-4xl md:text-7xl font-bold text-white tracking-tighter" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
            {t('Talarar & Artistar')}
          </h2>
          <EditButton onClick={() => setEditing('speakers')} testId="edit-speakers-button" />
        </div>

        <div className={`grid grid-cols-1 ${cols} gap-5 md:gap-8 max-w-sm mx-auto`}>
          {speakers.map((s) => (
            <div key={s.id} data-testid={`speaker-card-${s.id}`} className="group relative rounded-3xl overflow-hidden bg-[#1a1424] border border-white/5">
              <div className="relative aspect-[4/5] overflow-hidden">
                {s.comingSoon ? (
                  <ComingSoonArt />
                ) : (
                  <img src={s.image} alt={`${s.name} – ${s.role.toLowerCase()} på Refuel Weekend 2027`} loading="lazy" decoding="async" width={697} height={894} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <p className="text-[#ff2e2e] font-semibold uppercase tracking-widest text-xs mb-2">{t(s.role)}</p>
                <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3 md:mb-4 leading-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
                  {t(s.name)}
                </h3>
                {s.comingSoon ? (
                  <span data-testid={`speaker-soon-badge-${s.id}`} className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-white/20 text-white/70 font-semibold text-sm">
                    <HelpCircle size={16} /> {t('Blir avslørt snart')}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSelected(s)}
                    data-testid={`speaker-info-button-${s.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#ff2e2e] text-[#ff2e2e] font-semibold text-sm hover:bg-[#ff2e2e] hover:text-black transition-colors"
                  >
                    <Info size={16} /> {t('Info')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 md:mt-20" data-testid="hosts-section">
          <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
            <h3 className="text-3xl md:text-5xl font-bold text-white tracking-tighter" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Møteleiarar')}</h3>
            <div className="flex items-center gap-3">
              {hosts.every((h) => h.comingSoon) && <span className="hidden sm:inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 text-white/50 text-xs font-bold uppercase tracking-widest"><HelpCircle size={14} /> {t('Blir avslørt snart')}</span>}
              <EditButton onClick={() => setEditing('hosts')} testId="edit-hosts-button" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-4xl">
            {hosts.map((h) => (
              <div key={h.id} data-testid={`host-card-${h.id}`} className={`relative rounded-3xl overflow-hidden bg-[#1a1424] border ${h.comingSoon ? 'border-dashed border-white/15' : 'border-white/5'} aspect-[4/5] md:aspect-[4/3]`}>
                {h.comingSoon || !h.image ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-5">
                    <span className="text-[#ff2e2e] font-bold text-6xl md:text-7xl leading-none drop-shadow-[0_0_30px_rgba(255,46,46,0.45)]" style={{ fontFamily: "'Oktah Round', sans-serif" }}>?</span>
                    <p className="text-[#ff2e2e] font-semibold uppercase tracking-widest text-[10px] md:text-xs mt-4">{t(h.role)}</p>
                    <p className="text-white font-bold text-lg md:text-2xl tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t(h.name)}</p>
                  </div>
                ) : (
                  <button type="button" onClick={() => setSelected(h)} className="absolute inset-0 text-left group" data-testid={`host-info-button-${h.id}`}>
                    <img src={h.image} alt={h.name} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <p className="text-[#ff2e2e] font-semibold uppercase tracking-widest text-[10px]">{t(h.role)}</p>
                      <p className="text-white font-bold text-lg md:text-2xl tracking-tight" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{h.name}</p>
                    </div>
                  </button>
                )}
              </div>
            ))}
          </div>
          <Updates />
        </div>
      </div>
      {editing === 'speakers' && <PeopleEditor contentKey="speakers" title="Rediger talarar & artistar" initial={content.speakers || DEFAULT_SPEAKERS} roles={['Talar', 'Konsertartist', 'Seminarhaldar', 'Lovsongsleiar']} onClose={() => setEditing(null)} />}
      {editing === 'hosts' && <PeopleEditor contentKey="hosts" title="Rediger møteleiarar" initial={content.hosts || DEFAULT_HOSTS} roles={['Møteleiar']} onClose={() => setEditing(null)} />}

      <SpeakerDialog speaker={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

export default Speakers;
